export type DosageParseConfidence = 'high' | 'medium' | 'low'

export interface ParsedDailyDose {
  // Units of the extracted dose amount per day. Null when frequency is unknown
  // or the sig has no countable amount. Zero when the sig is one-time.
  dose: number | null
  frequencyPerDay: number | null
  confidence: DosageParseConfidence
  isPrn: boolean
  isOneTime: boolean
  bucket: string | null
}

const UNIT_PATTERNS = [
  'capsule|cap',
  'tablet|tab|pill',
  'patch',
  'application',
  'puff',
  'spray',
  'drop',
  'ml|milliliter',
  'mg|milligram',
  'g|gram',
  'unit',
]

const UNIT_REGEX = new RegExp(
  `(\\d+(?:\\.\\d+)?(?:\\/\\d+)?)\\s*(${UNIT_PATTERNS.join('|')})`,
  'i'
)

interface FrequencyPattern {
  name: string
  priority: number
  matcher: RegExp | string[] | ((text: string) => boolean)
  calculate: (text: string, match?: RegExpMatchArray) => number
  confidence: DosageParseConfidence
  skip?: (text: string) => boolean
}

const MULTI_DAILY_SKIP = (text: string) =>
  /\b(twice|two|2|three|3|four|4)\s+(?:times\s+)?(?:a\s+|per\s+)?(?:day|daily)\b/.test(
    text
  ) || /\b(bid|tid|qid)\b/.test(text)

const FREQUENCY_PATTERNS: FrequencyPattern[] = [
  {
    name: 'QID',
    priority: 100,
    matcher: /\bqid\b/,
    calculate: () => 4,
    confidence: 'high',
  },
  {
    name: 'TID',
    priority: 100,
    matcher: /\btid\b/,
    calculate: () => 3,
    confidence: 'high',
  },
  {
    name: 'BID',
    priority: 100,
    matcher: /\bbid\b/,
    calculate: () => 2,
    confidence: 'high',
  },
  {
    name: 'QD',
    priority: 100,
    matcher: /\bqd\b/,
    calculate: () => 1,
    confidence: 'high',
  },
  {
    name: 'QPM/Bedtime',
    priority: 30,
    matcher: /\b(qpm|qhs|at bedtime|at nighttime|at night|nightly)\b/,
    calculate: () => 1,
    confidence: 'high',
    skip: MULTI_DAILY_SKIP,
  },
  {
    name: 'QAM/Morning',
    priority: 30,
    matcher: /\b(qam|every morning|in the morning)\b/,
    calculate: () => 1,
    confidence: 'high',
    skip: MULTI_DAILY_SKIP,
  },
  {
    name: 'QPM/Evening',
    priority: 30,
    matcher: /\b(every evening|in the evening)\b/,
    calculate: () => 1,
    confidence: 'high',
    skip: MULTI_DAILY_SKIP,
  },

  {
    name: 'Every 6 hours',
    priority: 90,
    matcher: /every 6 hours/,
    calculate: () => 4,
    confidence: 'high',
  },
  {
    name: 'Every 8 hours',
    priority: 90,
    matcher: /every 8 hours/,
    calculate: () => 3,
    confidence: 'high',
  },
  {
    name: 'Every 12 hours',
    priority: 90,
    matcher: /every 12 hours/,
    calculate: () => 2,
    confidence: 'high',
  },
  {
    name: 'Every X hours (qNh)',
    priority: 88,
    matcher: /\bq\s*(\d+)\s*h(?:ours?|rs?)?\b/,
    calculate: (_text, match) => hoursToDailyFrequency(match?.[1]),
    confidence: 'high',
  },
  {
    name: 'Every X hours (generic)',
    priority: 85,
    matcher: /every (\d+) hours/,
    calculate: (_text, match) => hoursToDailyFrequency(match?.[1]),
    confidence: 'high',
  },

  {
    name: 'Every other day',
    priority: 80,
    matcher: ['every other day', 'every second day', 'alternate days'],
    calculate: () => 0.5,
    confidence: 'high',
  },
  {
    name: 'Twice weekly',
    priority: 75,
    matcher: ['twice weekly', '2 times weekly'],
    calculate: () => 2 / 7,
    confidence: 'high',
  },
  {
    name: 'Three times weekly',
    priority: 75,
    matcher: ['three times weekly', '3 times weekly'],
    calculate: () => 3 / 7,
    confidence: 'high',
  },
  {
    name: 'X times weekly',
    priority: 70,
    matcher: /(\d+)\s*times?\s*weekly/,
    calculate: (_text, match) => {
      if (!match) {
        return 1
      }
      const timesPerWeek = Number.parseInt(match[1], 10)
      return timesPerWeek / 7
    },
    confidence: 'high',
  },

  {
    name: 'Twice daily (text)',
    priority: 60,
    matcher: [
      'twice daily',
      'twice per day',
      'twice a day',
      '2 times per day',
      '2 times a day',
      'two times a day',
      'two times daily',
    ],
    calculate: () => 2,
    confidence: 'medium',
  },
  {
    name: 'Three times daily (text)',
    priority: 60,
    matcher: ['three times', '3 times'],
    calculate: () => 3,
    confidence: 'medium',
  },
  {
    name: 'Four times daily (text)',
    priority: 60,
    matcher: ['four times', '4 times'],
    calculate: () => 4,
    confidence: 'medium',
  },
  {
    name: 'Once daily (explicit)',
    priority: 55,
    matcher: /\b(once daily|once a day|every day|each day)\b/,
    calculate: () => 1,
    confidence: 'high',
  },
  {
    name: 'Daily',
    priority: 50,
    matcher: /\bdaily\b/,
    calculate: () => 1,
    confidence: 'high',
    skip: MULTI_DAILY_SKIP,
  },
  {
    name: 'One time',
    priority: 45,
    matcher: /\bone[-\s]time\b/,
    calculate: () => 0,
    confidence: 'high',
  },

  {
    name: 'Weekly',
    priority: 40,
    matcher: ['weekly', 'once per week', 'once a week'],
    calculate: () => 1 / 7,
    confidence: 'high',
  },
  {
    name: 'Every X days',
    priority: 35,
    matcher: /every (\d+) days/,
    calculate: (_text, match) => {
      const days = positiveInterval(match?.[1])
      return days == null ? Number.NaN : 1 / days
    },
    confidence: 'high',
  },
]

const SORTED_FREQUENCY_PATTERNS = [...FREQUENCY_PATTERNS].sort(
  (left, right) => right.priority - left.priority
)

function positiveInterval(text: string | undefined): number | null {
  if (!text) {
    return null
  }
  const value = Number.parseInt(text, 10)
  if (!Number.isFinite(value) || value <= 0) {
    return null
  }
  return value
}

function hoursToDailyFrequency(hoursText: string | undefined): number {
  const hours = positiveInterval(hoursText)
  return hours == null ? Number.NaN : 24 / hours
}

interface FrequencyPatternMatch {
  matched: boolean
  regexMatch?: RegExpMatchArray
}

function matchFrequencyPattern(
  pattern: FrequencyPattern,
  text: string
): FrequencyPatternMatch {
  if (pattern.skip?.(text)) {
    return { matched: false }
  }

  if (pattern.matcher instanceof RegExp) {
    const match = text.match(pattern.matcher)
    return match ? { matched: true, regexMatch: match } : { matched: false }
  }

  if (Array.isArray(pattern.matcher)) {
    const matched = pattern.matcher.some((candidate) =>
      text.includes(candidate)
    )
    return { matched }
  }

  if (typeof pattern.matcher === 'function') {
    return { matched: pattern.matcher(text) }
  }

  return { matched: false }
}

function parseFractionalAmount(amountStr: string): number | null {
  if (amountStr.includes('/')) {
    const [numerator, denominator] = amountStr.split('/').map(Number)
    if (
      !Number.isFinite(numerator) ||
      !Number.isFinite(denominator) ||
      denominator === 0
    ) {
      return null
    }
    return numerator / denominator
  }
  const value = Number.parseFloat(amountStr)
  return Number.isFinite(value) ? value : null
}

export function isPrnText(text: string): boolean {
  return /\b(as needed|as-needed|prn|when needed)\b/.test(text)
}

// Parses a sig into a daily dose. Frequency is the stated schedule (maximum
// for PRN). One-time and unmatched sigs do not invent a chronic daily dose.
export function parseDailyDoseFromText(dosageText: string): ParsedDailyDose {
  const lower = dosageText.toLowerCase()
  const isPrn = isPrnText(lower)
  const isPatch = lower.includes('patch') || lower.includes('transdermally')

  const amountMatch = lower.match(UNIT_REGEX)
  const baseAmount = amountMatch ? parseFractionalAmount(amountMatch[1]) : null

  if (isPatch) {
    const changeMatch = lower.match(/change every (\d+) days/)
    const changeDays = positiveInterval(changeMatch?.[1])
    if (changeDays != null) {
      const frequencyPerDay = 1 / changeDays
      return {
        dose: baseAmount == null ? null : baseAmount * frequencyPerDay,
        frequencyPerDay,
        confidence: 'high',
        isPrn,
        isOneTime: false,
        bucket: 'Patch change interval',
      }
    }
  }

  for (const pattern of SORTED_FREQUENCY_PATTERNS) {
    const matchResult = matchFrequencyPattern(pattern, lower)
    if (!matchResult.matched) {
      continue
    }

    const frequencyPerDay = pattern.calculate(lower, matchResult.regexMatch)
    if (!Number.isFinite(frequencyPerDay)) {
      continue
    }
    const isOneTime = pattern.name === 'One time'
    let dose: number | null = null
    if (isOneTime) {
      dose = 0
    } else if (baseAmount != null) {
      const daily = baseAmount * frequencyPerDay
      dose = Number.isFinite(daily) ? daily : null
    }

    return {
      dose,
      frequencyPerDay,
      confidence: isPatch ? 'medium' : pattern.confidence,
      isPrn,
      isOneTime,
      bucket: pattern.name,
    }
  }

  return {
    dose: null,
    frequencyPerDay: null,
    confidence: 'low',
    isPrn,
    isOneTime: false,
    bucket: null,
  }
}
